import React, { useEffect, useRef, useState } from 'react';
import Styles from '../../assets/css/components/company/companyList.module.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiEdit } from "react-icons/bi";
import { LiaEyeSolid } from "react-icons/lia";
import { RiFilter2Line } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import { AiOutlineSearch } from "react-icons/ai";
import { fetchCompanies, setFilters, resetFilters } from '../../features/company/companySlice';
import Loader from '../../utils/Loader';

const CompanyList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { companies, loading, error, filters } = useSelector((state) => state.company);

    const [isOpen, setIsOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [filterDate, setFilterDate] = useState({
        from: '',
        to: ''
    });

    const dropdownRefs = useRef({});
    const filterDropdownRef = useRef(null);
    const filterButtonRef = useRef(null);
    const [dateError, setDateError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dropDownCompanyManage, setDropDownCompanyManage] = useState({
        id: 0,
        open: false
    });

    useEffect(() => {
        dispatch(resetFilters());
        dispatch(fetchCompanies());
    }, [dispatch]);

    const dateStatusApply = () => {
        // Validate that both dates are provided if any is provided
        if ((filterDate.from && !filterDate.to) || (!filterDate.from && filterDate.to)) {
            setDateError('Both From and To dates are required');
            return;
        }

        if (filterDate.from && filterDate.to && new Date(filterDate.from) > new Date(filterDate.to)) {
            setDateError("From date cannot be after To date");
            return;
        }

        dispatch(setFilters({
            ...filters,
            from: filterDate.from,
            to: filterDate.to,
            status: statusFilter,
            page: "1"
        }));
        dispatch(fetchCompanies());
        setIsOpen(false);
    };

    const handleClear = () => {
        setFilterDate({ from: '', to: '' });
        setStatusFilter('');
        setDateError('');
    };

    const handleReset = () => {
        setFilterDate({ from: '', to: '' });
        setStatusFilter('');
        setSearchTerm('');
        setDateError('');
    };

    const handleSearch = (value) => {
        setSearchTerm(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            dispatch(setFilters({
                ...filters,
                search: value,
                page: "1"
            }));
            dispatch(fetchCompanies());
        }, 500);
    };

    const handleDateChange = (type, value) => {
        setFilterDate(prev => ({
            ...prev,
            [type]: value
        }));
        setDateError('');
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
    };

    // Fixed dropdown handler with event propagation control
    const handleDropDown = (e) => {
        e.stopPropagation();
        let value = e.currentTarget.dataset.userId;
        if (dropDownCompanyManage.id === value) {
            setDropDownCompanyManage({ ...dropDownCompanyManage, open: !dropDownCompanyManage.open });
        } else {
            setDropDownCompanyManage({ id: value, open: true });
        }
    };

    // Fixed navigation handlers
    const handleView = (id, event) => {
        event.stopPropagation();
        setDropDownCompanyManage({ id: 0, open: false });
        navigate(`/company/view/${id}`);
    };

    const handleEdit = (id, event) => {
        event.stopPropagation();
        setDropDownCompanyManage({ id: 0, open: false });
        navigate(`/company/edit/${id}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    // Improved click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Handle company dropdown closing
            const isDropdownClick = event.target.closest(`.${Styles.manage}`) ||
                event.target.closest(`.${Styles.manageUser}`);

            if (!isDropdownClick) {
                setDropDownCompanyManage({ id: 0, open: false });
            }

            // Handle filter dropdown closing with better detection
            if (isOpen) {
                const isFilterDropdownClick = filterDropdownRef.current && filterDropdownRef.current.contains(event.target);
                const isFilterButtonClick = filterButtonRef.current && filterButtonRef.current.contains(event.target);
                const isDatePickerClick = event.target.type === 'date' ||
                    event.target.closest('input[type="date"]') ||
                    event.target.closest('.react-datepicker') ||
                    event.target.closest('[data-testid="date-picker"]') ||
                    // Check for common date picker class names
                    event.target.closest('.datepicker') ||
                    event.target.closest('.date-picker') ||
                    event.target.closest('.calendar') ||
                    // Check if it's a date picker popup/overlay
                    event.target.closest('[role="dialog"]') ||
                    event.target.closest('[role="listbox"]') ||
                    // Browser native date picker detection
                    event.target.tagName === 'OPTION' ||
                    (event.target.tagName === 'INPUT' && event.target.type === 'date');

                if (!isFilterDropdownClick && !isFilterButtonClick && !isDatePickerClick) {
                    setIsOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (error) {
        return <div className={`${Styles.error}`}>Error: {error}</div>;
    }

    return (
        <div className={`${Styles.container}`}>

            {/* Filter And Search Data */}
            <div className={`${Styles.filter_search_btn}`}>
                <div className={`${Styles.filter}`}>
                    <div className={`${Styles.sub_filter}`}>
                        <button
                            ref={filterButtonRef}
                            onClick={() => { setIsOpen(!isOpen); }}
                        >
                            <RiFilter2Line /> Filter
                        </button>
                        {isOpen && (
                            <div className={`${Styles.filter_dropdown}`} ref={filterDropdownRef}>
                                <div className={`${Styles.heading}`}>
                                    <p>Filter</p>
                                    <IoMdClose onClick={() => setIsOpen(!isOpen)} />
                                </div>

                                <div className={`${Styles.selectDate}`}>
                                    <div className={`${Styles.heading_clear}`}>
                                        <p>Select Date Range</p>
                                        <p onClick={handleClear}>Clear</p>
                                    </div>
                                    <div className={`${Styles.filter_date}`}>
                                        <div className={`${Styles.from}`}>
                                            <label htmlFor="from">From:</label>
                                            <input
                                                type="date"
                                                id='from'
                                                placeholder='00-00-0000'
                                                value={filterDate.from}
                                                max={filterDate.to || undefined}
                                                onChange={(e) => handleDateChange('from', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className={`${Styles.to}`}>
                                            <label htmlFor="to">To:</label>
                                            <input
                                                type="date"
                                                id='to'
                                                placeholder='00-00-0000'
                                                value={filterDate.to}
                                                min={filterDate.from || undefined}
                                                onChange={(e) => handleDateChange('to', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    {dateError && <div className={`${Styles.date_error}`} style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{dateError}</div>}
                                </div>

                                <div className={`${Styles.selectStatus}`}>
                                    <div className={`${Styles.heading_clear}`}>
                                        <p>Select Status</p>
                                        <p onClick={() => handleStatusChange('')}>Clear</p>
                                    </div>
                                    <select
                                        name="statusFilter"
                                        id="statusFilter"
                                        value={statusFilter}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <option value="" hidden>**** Select Status ****</option>
                                        <option value="1">Active</option>
                                        <option value="2">Inactive</option>
                                    </select>
                                </div>

                                <div className={`${Styles.buttons}`}>
                                    <button onClick={handleReset}>Reset</button>
                                    <button onClick={dateStatusApply}>Apply Now</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${Styles.search}`}>
                    <div className={`${Styles.inputContainer}`}>
                        <AiOutlineSearch className={`${Styles.searchIcon}`} />
                        <input
                            type="text"
                            placeholder="Search by Company Name"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Item Table */}
            <div className={`${Styles.product}`}>
                <div className={`${Styles.item_table}`}>
                    {loading ? (
                        <div className={`${Styles.loader_container}`}>
                            <Loader size="large" color="primary" />
                        </div>
                    ) : error ? (
                        <div className={`${Styles.error_message}`}>
                            {error}
                        </div>
                    ) : companies.length === 0 ? (
                        <div className={`${Styles.no_data}`}>
                            No companies found
                        </div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th className={`${Styles.first_row}`}>No.</th>
                                    <th>Company Name</th>
                                    <th>Company Email</th>
                                    <th>Status</th>
                                    <th>Created date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td className={`${Styles.first_row}`}>{index + 1}</td>
                                        <td>{item.company_name}</td>
                                        <td>{item.email}</td>
                                        <td className={`${Styles.status_badge} ${item.status === 1 ? Styles.active : Styles.inactive}`}>
                                            {item.status === 1 ? 'Active' : 'Inactive'}
                                        </td>
                                        <td>{formatDate(item.created_at)}</td>
                                        <td>
                                            <div className={`${Styles.manage}`} ref={el => dropdownRefs.current[item.id] = el}>
                                                <BsThreeDotsVertical data-userId={item.id} onClick={handleDropDown} />
                                                {
                                                    dropDownCompanyManage.id == item.id && dropDownCompanyManage.open && (
                                                        <div className={`${Styles.manageUser}`}>
                                                            <p onClick={(e) => handleView(item.id, e)}>
                                                                <span><LiaEyeSolid /></span>
                                                                <span>View</span>
                                                            </p>
                                                            <p onClick={(e) => handleEdit(item.id, e)}>
                                                                <span><BiEdit /></span>
                                                                <span>Edit</span>
                                                            </p>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyList
